import { type MouseEvent, useEffect, useEffectEvent, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { logToSentry, useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import {
    AlgoliaAnalyticsEventAction,
    AlgoliaAnalyticsEventPage,
    AlgoliaAnalyticsEventSource,
    GA4EventName,
    GA4FeatureCategory,
    type GA4ItemListName,
    sendAlgoliaObjectIdsClickedEvent,
    sendAlgoliaObjectIdsViewedEvent,
    trackEecProductClick,
    trackEecProductImpression,
} from '@jsmdg/tracking';
import { Image, PureLink, type SliderSlide } from '@jsmdg/yoshi';
import { type FragmentContext } from '../../../shared/types/fragmentContext';
import { type SearchResult } from '../../../shared/types/searchResult';
import { enhanceUrlWithSearchParams } from '../../helper/enhanceUrlWithSearchParams';
import { useImpressionTracking } from '../../hooks/useImpressionTracking';
import { useInsightsClient } from '../../hooks/useInsightsClient';
import { useProductTileData } from '../../hooks/useProductTileData';
import { getPrimaryImage } from './helpers';
import { MainVoucherSlider } from './MainVoucherSlider';
import { ThumbnailSlider } from './ThumbnailSlider';
import { VoucherProductDetails } from './VoucherProductDetails';
import styles from './BestsellerValueVoucherSlider.module.scss';

type BestsellerValueVoucherSliderProps = {
    readonly valueVoucherSearchResult: SearchResult;
};

const VALUE_VOUCHER_SLIDER_ITEM_LIST_NAME = 'value voucher slider' as GA4ItemListName; // Since this is an A/B test, I will keep as it is for now
const SLIDE_IMPRESSION_DEBOUNCE_MS = 300;

const messages = defineMessages({
    headerTitle: { defaultMessage: 'Mit unseren Wertgutscheinen' },
    subtitle: {
        defaultMessage: 'Freie Wahl verschenken',
    },
    headerTitleDesktop: {
        defaultMessage: 'Freie Wahl verschenken - mit unseren Wertgutscheinen',
    },
    subtitleDesktop: {
        defaultMessage:
            'Ein Gutschein. 70+ verschiedene Motive. Das perfekte Geschenk für Unentschlossene!',
    },
    a11yPreviousSlide: { defaultMessage: 'Zur vorherigen Seite' },
    a11yNextSlide: { defaultMessage: 'Zur nächsten Seite' },
});

const BestsellerValueVoucherSlider = ({
    valueVoucherSearchResult,
}: BestsellerValueVoucherSliderProps): JSX.Element => {
    const intl = useIntl();
    const { algoliaTracking, isMydays } = useFragmentContext<FragmentContext>();
    const insightsClientRef = useInsightsClient();
    const products = valueVoucherSearchResult.items;
    const [activeSlideIndex, setActiveSlideIndex] = useState(0);
    const activeProduct = products[activeSlideIndex] ?? products[0];
    const activeSlidePosition = activeSlideIndex + 1;
    const hasBeenVisibleRef = useRef(false);
    const previousTrackingKeyRef = useRef<string | null>(null);
    const { productTrackingData, url } = useProductTileData(activeProduct, intl, {
        trackingListName: VALUE_VOUCHER_SLIDER_ITEM_LIST_NAME,
    });

    const productTrackingPayload = {
        product: productTrackingData,
        position: activeSlidePosition,
        list: VALUE_VOUCHER_SLIDER_ITEM_LIST_NAME,
        featureCategory: GA4FeatureCategory.ProductList,
        itemListName: VALUE_VOUCHER_SLIDER_ITEM_LIST_NAME,
    };

    const sendObjectEvent = async (
        action: AlgoliaAnalyticsEventAction,
        productId?: string,
    ): Promise<void> => {
        if (!productId || !algoliaTracking?.userToken || !algoliaTracking.indexName) {
            return;
        }

        try {
            const insightsClient = (await insightsClientRef.current)?.default;

            if (!insightsClient) {
                return;
            }

            const eventName = {
                action,
                page: AlgoliaAnalyticsEventPage.Home,
                source: AlgoliaAnalyticsEventSource.Bestseller,
            };

            if (action === AlgoliaAnalyticsEventAction.ProductViewed) {
                sendAlgoliaObjectIdsViewedEvent({
                    eventName,
                    indexName: algoliaTracking.indexName,
                    insightsClient,
                    objectIds: [productId],
                });
                return;
            }

            sendAlgoliaObjectIdsClickedEvent({
                eventName,
                indexName: algoliaTracking.indexName,
                insightsClient,
                objectIds: [productId],
            });
        } catch (error) {
            logToSentry(error instanceof Error ? error : new Error('Algolia Insights failed'));
        }
    };

    const trackSlideImpression = useEffectEvent(() => {
        trackEecProductImpression({
            ...productTrackingPayload,
            eventName: GA4EventName.ViewItemList,
        });
        void sendObjectEvent(AlgoliaAnalyticsEventAction.ProductViewed, activeProduct?.productId);
    });

    // Debounced slide impressions, skip until the slider has been in view once
    useEffect(() => {
        if (!hasBeenVisibleRef.current) {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            const trackingKey = `${activeProduct.productId}-${activeSlidePosition}`;

            if (previousTrackingKeyRef.current === trackingKey) {
                return;
            }

            previousTrackingKeyRef.current = trackingKey;
            trackSlideImpression();
        }, SLIDE_IMPRESSION_DEBOUNCE_MS);

        return () => {
            window.clearTimeout(timeoutId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- trackSlideImpression is stable via useEffectEvent
    }, [activeProduct.productId, activeSlidePosition]);

    const setImpressionTrackedElement = useImpressionTracking(() => {
        hasBeenVisibleRef.current = true;
        // Remember this slide so the change effect does not track it again
        previousTrackingKeyRef.current = `${activeProduct.productId}-${activeSlidePosition}`;
        trackSlideImpression();
    });

    const onActiveProductClick = async (
        event?: MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
    ): Promise<void> => {
        event?.preventDefault();
        await sendObjectEvent(AlgoliaAnalyticsEventAction.ProductClicked, activeProduct.productId);
        window.location.href = enhanceUrlWithSearchParams(url);
        await window.yieldToMainThread();
        trackEecProductClick(productTrackingPayload);
    };

    const a11yPreviousSlide = intl.formatMessage(messages.a11yPreviousSlide);
    const a11yNextSlide = intl.formatMessage(messages.a11yNextSlide);

    const mainSlides: SliderSlide[] = products.map((product, index) => {
        const image = product.mainImage ?? getPrimaryImage(product);
        const isActiveSlide = index === activeSlideIndex;

        return {
            key: product.productId,
            content: (
                <PureLink
                    className="d-flex w-100"
                    href={product.url}
                    internal
                    onClick={isActiveSlide ? onActiveProductClick : undefined}
                >
                    <Image
                        alt={image.alt}
                        className={classNames('w-100', styles.mainImage)}
                        lazyLoad
                        sizes={image.sizes}
                        src={image.url}
                        srcSet={image.srcset}
                    />
                </PureLink>
            ),
        };
    });

    return (
        <div
            ref={setImpressionTrackedElement}
            className="mt-4x"
            data-testid="bestseller-value-voucher-slider"
        >
            <div className="mb-3x">
                <div className="d-sm-none">
                    <p className={styles.subtitle}>{intl.formatMessage(messages.subtitle)}</p>
                    <p className={classNames('fw-semibold', styles.headerTitle)}>
                        {intl.formatMessage(messages.headerTitle)}
                    </p>
                </div>

                <div
                    className={classNames('d-none d-sm-block', {
                        'text-center': isMydays,
                    })}
                >
                    <p className={styles.headerTitleDesktop}>
                        {intl.formatMessage(messages.headerTitleDesktop)}
                    </p>
                    <p className={styles.subtitleDesktop}>
                        {intl.formatMessage(messages.subtitleDesktop)}
                    </p>
                </div>
            </div>

            <div className={classNames('overflow-hidden', styles.card)}>
                <div className={classNames('grid', styles.cardGrid)}>
                    <div
                        className={classNames(
                            'd-flex align-items-center py-2x py-sm-3x',
                            styles.mainSliderColumn,
                        )}
                    >
                        <div className="overflow-hidden w-100">
                            <MainVoucherSlider
                                a11yNextSlide={a11yNextSlide}
                                a11yPreviousSlide={a11yPreviousSlide}
                                activeSlideIndex={activeSlideIndex}
                                onSlideChange={setActiveSlideIndex}
                                slides={mainSlides}
                            />
                        </div>
                    </div>

                    <div className="d-flex flex-column gap-2x py-2x py-sm-3x">
                        <div className="px-2x pl-sm-0">
                            <ThumbnailSlider
                                a11yNextSlide={a11yNextSlide}
                                a11yPreviousSlide={a11yPreviousSlide}
                                activeSlideIndex={activeSlideIndex}
                                onSlideChange={setActiveSlideIndex}
                                products={products}
                            />
                        </div>

                        <VoucherProductDetails
                            onSelectAmountClick={onActiveProductClick}
                            product={activeProduct}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export { BestsellerValueVoucherSlider };
